/**
 * One quote: sections, the margin slider, and acceptance.
 *
 * Acceptance is the biggest button in the app and the one with the most
 * consequences, so it confirms first and then reports exactly what it created
 * rather than silently navigating away.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "react-router-dom";

import { ApiError, api, openDocument } from "../lib/api";
import { OFFICE, useAuth } from "../lib/auth";
import { day, money, percent, titleCase } from "../lib/format";
import * as pricing from "../lib/pricing";
import type { QuoteOut, SendQuoteResult } from "../lib/types";
import { AdjustmentEditor } from "../components/AdjustmentEditor";
import { QuoteHeaderCard, QuoteSections, canEditQuote } from "../components/QuoteEditor";
import { Card, Chip, ErrorState, Loading, PageHeader } from "../components/ui";

export default function QuoteDetail() {
  const { id = "" } = useParams();
  const queryClient = useQueryClient();
  const { can } = useAuth();
  const isOffice = can(...OFFICE);

  const [margin, setMargin] = useState<number | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const {
    data: quote,
    isPending,
    error,
    refetch,
  } = useQuery({
    queryKey: ["quote", id],
    queryFn: () => api.quotes.get(id),
    enabled: Boolean(id),
  });

  // Seed the slider from the quote once it lands, then leave it alone so
  // dragging it does not fight the refetch.
  useEffect(() => {
    if (quote && margin === null) setMargin(quote.target_margin_bp);
  }, [quote, margin]);

  const reprice = useMutation({
    mutationFn: (bp: number) => api.quotes.reprice(id, bp),
    onSuccess: (fresh) => queryClient.setQueryData(["quote", id], fresh),
    onError: (err) => setProblem(err instanceof ApiError ? err.message : "Couldn't reprice."),
  });

  const accept = useMutation({
    mutationFn: () => api.quotes.accept(id, { take_deposit: true }),
    onSuccess: () => {
      setConfirming(false);
      void queryClient.invalidateQueries({ queryKey: ["quote", id] });
      void queryClient.invalidateQueries({ queryKey: ["jobs"] });
    },
    onError: (err) => {
      setConfirming(false);
      setProblem(err instanceof ApiError ? err.message : "Couldn't accept this quote.");
    },
  });

  const changePricing = useMutation({
    mutationFn: (mode: "price_book" | "target_margin") => api.quotes.update(id, { pricing: mode }),
    onSuccess: (fresh) => {
      setProblem(null);
      queryClient.setQueryData<QuoteOut>(["quote", id], fresh);
      void queryClient.invalidateQueries({ queryKey: ["quotes"] });
    },
    onError: (err) => setProblem(err instanceof ApiError ? err.message : "Couldn't change how it's priced."),
  });

  if (isPending) return <Loading what="Loading the quote" />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const accepted = quote.status === "accepted";
  const priceBook = (quote.pricing ?? "price_book") === "price_book";
  const adjustment = quote.adjustment_amount_cents ?? 0;
  const result = accept.data;

  return (
    <div className="page">
      <PageHeader eyebrow={quote.number} title={quote.title}>
        {quote.built_on_site ? "Built on site." : "Built in the office."} {quote.sections.length} section
        {quote.sections.length === 1 ? "" : "s"}.
      </PageHeader>

      {problem && (
        <p className="notice notice-crit" role="alert">
          {problem}
        </p>
      )}

      {result && (
        <div className="notice notice-info" role="status">
          <b>Accepted.</b> That one signature created {result.job_ids.length} job
          {result.job_ids.length === 1 ? "" : "s"}, {result.asset_ids.length} asset
          {result.asset_ids.length === 1 ? "" : "s"}
          {result.contract_id ? ", a maintenance contract" : ""}
          {result.purchase_order_ids.length > 0
            ? `, ${result.purchase_order_ids.length} draft purchase order${result.purchase_order_ids.length === 1 ? "" : "s"}`
            : ""}
          {result.deposit_invoice_id ? " and a deposit invoice" : ""}. <Link to="/jobs">See the jobs</Link>.
        </div>
      )}

      <div className="split">
        <div className="split-main">
          <QuoteSections
            quote={quote}
            editable={isOffice && canEditQuote(quote)}
            onChanged={(next) => {
              queryClient.setQueryData<QuoteOut>(["quote", id], next);
              void queryClient.invalidateQueries({ queryKey: ["quotes"] });
            }}
          />
        </div>

        <aside className="split-side">
          <Card title="Price">
            <div className="quote-pricing">
              {isOffice && canEditQuote(quote) ? (
                <label className="field">
                  <span>Prices</span>
                  <select
                    value={quote.pricing ?? "price_book"}
                    disabled={changePricing.isPending}
                    onChange={(e) => changePricing.mutate(e.target.value as "price_book" | "target_margin")}
                  >
                    <option value="price_book">From the price book</option>
                    <option value="target_margin">Target margin (older quotes)</option>
                  </select>
                </label>
              ) : (
                <p className="tiny">
                  Prices: {priceBook ? "from the price book" : "target margin (older quotes)"}
                </p>
              )}
            </div>
            <dl className="kv kv-money">
              <dt>Cost</dt>
              <dd className="mono">{money(quote.cost_cents)}</dd>
              {/* Already inside the subtotal the server sends; shown, not added. */}
              {adjustment !== 0 && (
                <>
                  <dt>{pricing.adjustmentLabel(quote.adjustment_bp, quote.adjustment_cents, quote.adjustment_label)}</dt>
                  <dd className="mono">{money(adjustment)}</dd>
                  <dt>Subtotal</dt>
                  <dd className="mono">{money(quote.subtotal_cents)}</dd>
                </>
              )}
              <dt>Margin {percent(quote.target_margin_bp, 0)}</dt>
              <dd className="mono">{money(quote.subtotal_cents - quote.cost_cents)}</dd>
              <dt>GST</dt>
              <dd className="mono">{money(quote.tax_cents)}</dd>
              <dt className="total">Total</dt>
              <dd className="mono total">{money(quote.total_cents)}</dd>
            </dl>

            {can("owner", "office") && canEditQuote(quote) && (
              <AdjustmentEditor
                bp={quote.adjustment_bp}
                cents={quote.adjustment_cents}
                label={quote.adjustment_label}
                idPrefix="quote-adjustment"
                onSave={async (next) => {
                  const fresh = await api.quotes.update(id, {
                    adjustment_bp: next.bp,
                    adjustment_cents: next.cents,
                    adjustment_label: next.label,
                  });
                  setProblem(null);
                  queryClient.setQueryData<QuoteOut>(["quote", id], fresh);
                  void queryClient.invalidateQueries({ queryKey: ["quotes"] });
                }}
              />
            )}

            {isOffice && !accepted && (
              <div style={{ marginTop: 16 }}>
                <label className="field">
                  <span>
                    Target margin <b className="mono">{percent(margin ?? quote.target_margin_bp, 0)}</b>
                  </span>
                  <input
                    className="slider"
                    type="range"
                    min={1800}
                    max={6200}
                    step={100}
                    value={margin ?? quote.target_margin_bp}
                    onChange={(e) => setMargin(Number(e.target.value))}
                    onPointerUp={() => margin !== null && reprice.mutate(margin)}
                    onKeyUp={() => margin !== null && reprice.mutate(margin)}
                  />
                </label>
                {reprice.isPending && <p className="tiny muted">Repricing…</p>}
                {priceBook && <p className="tiny muted">Only lines with no price-book price use this.</p>}
                <p className="tiny muted">
                  Re-derives the sell side from cost, so the quote stays coherent at any margin rather than scaling
                  prices that were individually right.
                </p>
              </div>
            )}
          </Card>

          {isOffice && canEditQuote(quote) && (
            <QuoteHeaderCard
              key={`${quote.title}|${quote.deposit_pct_bp}|${quote.valid_until ?? ""}`}
              quote={quote}
              onChanged={(next) => {
                queryClient.setQueryData<QuoteOut>(["quote", id], next);
              }}
            />
          )}

          <SendCard quote={quote} isOffice={isOffice} onChanged={() => void refetch()} />

          <Card title="Acceptance">
            {accepted ? (
              <p className="tiny">
                <Chip tone="ok" dot>
                  Accepted
                </Chip>{" "}
                {quote.accepted_by_name ? `by ${quote.accepted_by_name}` : ""}
                {quote.accepted_at ? ` on ${day(quote.accepted_at)}` : ""}. Everything it implies has been created.
              </p>
            ) : !isOffice ? (
              <p className="tiny muted">Quotes are accepted by the office. Ask whoever handles approvals.</p>
            ) : confirming ? (
              <>
                <p className="tiny" style={{ marginBottom: 10 }}>
                  This creates the jobs, registers the assets, commits stock, drafts any purchase order, starts the
                  contract and raises the deposit — in one go.
                </p>
                <div className="btn-row">
                  <button
                    className="btn btn-primary"
                    type="button"
                    onClick={() => accept.mutate()}
                    disabled={accept.isPending}
                  >
                    {accept.isPending ? "Accepting…" : "Yes, accept it"}
                  </button>
                  <button className="btn" type="button" onClick={() => setConfirming(false)}>
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <button className="btn btn-primary btn-block" type="button" onClick={() => setConfirming(true)}>
                Accept quote
              </button>
            )}

            {quote.deposit_pct_bp > 0 && !accepted && (
              <p className="tiny muted" style={{ marginTop: 10 }}>
                A {percent(quote.deposit_pct_bp, 0)} deposit invoice will be raised —{" "}
                {money(
                  Math.round((quote.subtotal_cents * quote.deposit_pct_bp) / 10000) +
                    Math.round((Math.round((quote.subtotal_cents * quote.deposit_pct_bp) / 10000) * 1000) / 10000),
                )}{" "}
                including GST.
              </p>
            )}
          </Card>

          {accepted && <QuoteJobs quoteId={quote.id} />}

          <Card title="Status">
            <Chip tone={accepted ? "ok" : "neutral"} dot>
              {titleCase(quote.status)}
            </Chip>
          </Card>
        </aside>
      </div>
    </div>
  );
}

/**
 * Sending the link, and saying plainly what happened.
 *
 * The result is deliberately three-valued rather than a green tick: sent,
 * not configured, and tried-and-failed are different situations with different
 * fixes, and a UI that shows the same thing for all three is how a quote sits
 * in a drafts folder for a week while everyone assumes it went.
 *
 * It asks who, never how (QUOTE-10). The customer's contacts are offered as
 * chips; an address gets the email with the PDF and a mobile gets a text with
 * the link, and the button says which it is about to do. A landline, or
 * somebody who has asked not to be texted, is still shown — the office may
 * want to ring them — with the reason it cannot be used.
 */
function SendBox({
  quoteId,
  to,
  note,
  onTo,
  onNote,
  busy,
  outcome,
  onSend,
}: {
  quoteId: string;
  to: string;
  note: string;
  onTo: (value: string) => void;
  onNote: (value: string) => void;
  busy: boolean;
  outcome: SendQuoteResult | null;
  onSend: () => void;
}) {
  const people = useQuery({
    queryKey: ["quote-recipients", quoteId],
    queryFn: () => api.quotes.recipients(quoteId),
  });
  const typed = to.trim();
  const texting = typed.length > 0 && !typed.includes("@");
  const usable = texting ? typed.replace(/\D/g, "").length >= 8 : /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(typed);

  return (
    <div style={{ marginTop: 14, borderTop: "1px solid var(--line-soft)", paddingTop: 12 }}>
      {(people.data ?? []).length > 0 && (
        <div className="chips" style={{ marginBottom: 10 }}>
          {(people.data ?? []).map((person) => (
            <span key={`${person.contact_id}-${person.email ?? person.phone}`} className="chip-row">
              {person.email && (
                <button type="button" className="chip chip-pick" onClick={() => onTo(person.email ?? "")}>
                  {person.name} · email
                </button>
              )}
              {person.phone && person.can_text && (
                <button type="button" className="chip chip-pick" onClick={() => onTo(person.phone ?? "")}>
                  {person.name} · text
                </button>
              )}
              {person.phone && !person.can_text && person.why && (
                <Chip tone="neutral">
                  {person.name} — {person.why}
                </Chip>
              )}
            </span>
          ))}
        </div>
      )}

      <label className="field">
        <span>Send it to</span>
        <input
          value={to}
          onChange={(e) => onTo(e.target.value)}
          placeholder="name@company.com.au, or 0412 345 678"
          autoComplete="off"
          inputMode={texting ? "tel" : "email"}
          disabled={busy}
        />
      </label>
      <label className="field">
        <span>Note (optional)</span>
        <input
          value={note}
          onChange={(e) => onNote(e.target.value)}
          placeholder="As discussed on site this morning."
          disabled={busy}
        />
      </label>
      <button className="btn btn-block" type="button" onClick={onSend} disabled={busy || !usable}>
        {busy ? "Sending…" : texting ? "Text the link" : "Send with the PDF"}
      </button>

      {outcome?.delivered_to && (
        <p className={`tiny ${outcome.delivered ? "" : "muted"}`} style={{ marginTop: 8 }} role="status">
          {outcome.delivered ? (
            <>Sent to {outcome.delivered_to}.</>
          ) : (
            <>
              <b>Not sent.</b> {outcome.delivery_detail}. The link above still works.
            </>
          )}
        </p>
      )}

      <Attempts quoteId={quoteId} after={outcome} />
    </div>
  );
}

/**
 * Every attempt, newest first.
 *
 * These rows have been written since the first version of sending and were
 * never readable, which left the office answering "I never got it" with a
 * shrug. With a second channel they are the whole story — emailed Tuesday,
 * bounced, texted Wednesday — so they are worth a few lines on the screen.
 */
function Attempts({ quoteId, after }: { quoteId: string; after: SendQuoteResult | null }) {
  const tried = useQuery({
    queryKey: ["quote-deliveries", quoteId, after?.delivery_detail ?? ""],
    queryFn: () => api.quotes.deliveries(quoteId),
  });
  const rows = tried.data ?? [];
  if (rows.length === 0) return null;

  return (
    <details className="tiny" style={{ marginTop: 10 }}>
      <summary className="muted">
        {rows.length} attempt{rows.length === 1 ? "" : "s"} to send this
      </summary>
      <ul className="stack tiny" style={{ marginTop: 6 }}>
        {rows.map((row, i) => (
          <li key={`${row.attempted_at}-${i}`}>
            <Chip tone={row.status === "sent" ? "ok" : row.status === "failed" ? "crit" : "warn"}>
              {row.channel === "sms" ? "text" : row.channel === "email" ? "email" : row.channel}
            </Chip>{" "}
            {row.to_address} · <span className="muted">{day(row.attempted_at.slice(0, 10))}</span>
            {row.status !== "sent" && row.detail ? <div className="muted">{row.detail}</div> : null}
          </li>
        ))}
      </ul>
    </details>
  );
}

/**
 * The link, and the two facts worth knowing about it: whether it went, and
 * whether anybody opened it.
 *
 * Keystone does not send the email. It hands over a URL, which is the part a
 * quoting system can be relied on for — an SMTP server that is misconfigured on
 * the day you need to send a quote is a worse failure than a copy and paste.
 */
function SendCard({ quote, isOffice, onChanged }: { quote: QuoteOut; isOffice: boolean; onChanged: () => void }) {
  const [copied, setCopied] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [emailTo, setEmailTo] = useState("");
  const [note, setNote] = useState("");
  const [outcome, setOutcome] = useState<SendQuoteResult | null>(null);

  const send = useMutation({
    mutationFn: (payload?: { to?: string; note?: string }) => api.quotes.send(quote.id, payload),
    onSuccess: (result) => {
      setOutcome(result);
      onChanged();
    },
    onError: (err) => setProblem(err instanceof ApiError ? err.message : "Couldn't send it."),
  });

  const rotate = useMutation({
    mutationFn: () => api.quotes.rotateLink(quote.id),
    onSuccess: onChanged,
    onError: (err) => setProblem(err instanceof ApiError ? err.message : "Couldn't rotate the link."),
  });

  // Absolute, because the whole point is that it gets pasted somewhere else.
  const url = quote.share_path ? `${window.location.origin}${quote.share_path}` : null;

  const copy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access is denied often enough — over plain http, in some
      // embedded browsers — that failing silently would look like a bug. The
      // link is on screen and selectable either way.
      setProblem("Couldn't copy automatically — select the link and copy it.");
    }
  };

  return (
    <Card title="Customer link">
      {problem && (
        <p className="tiny" style={{ color: "var(--crit)" }} role="alert">
          {problem}
        </p>
      )}

      {url ? (
        <>
          <p className="tiny mono" style={{ wordBreak: "break-all", marginBottom: 10 }}>
            {url}
          </p>
          <div className="btn-row">
            <button className="btn btn-primary" type="button" onClick={() => void copy()}>
              {copied ? "Copied" : "Copy link"}
            </button>
            <a className="btn" href={quote.share_path ?? "#"} target="_blank" rel="noreferrer">
              Preview
            </a>
          </div>
          <SendBox
            quoteId={quote.id}
            to={emailTo}
            note={note}
            onTo={setEmailTo}
            onNote={setNote}
            busy={send.isPending}
            outcome={outcome}
            onSend={() => send.mutate({ to: emailTo.trim(), note: note.trim() || undefined })}
          />

          <p className="tiny muted" style={{ marginTop: 10 }}>
            {quote.view_count > 0 ? (
              <>
                Opened {quote.view_count} time{quote.view_count === 1 ? "" : "s"}
                {quote.viewed_at ? `, first on ${day(quote.viewed_at)}` : ""}.
              </>
            ) : (
              "Not opened yet."
            )}
          </p>
          {isOffice && quote.status !== "accepted" && (
            <p className="tiny muted" style={{ marginTop: 8 }}>
              <button className="link-button" type="button" onClick={() => rotate.mutate()} disabled={rotate.isPending}>
                Rotate the link
              </button>{" "}
              if it went to the wrong address. The old one stops working immediately.
            </p>
          )}
        </>
      ) : (
        <>
          <button
            className="btn btn-primary btn-block"
            type="button"
            onClick={() => send.mutate(undefined)}
            disabled={send.isPending || quote.sections.length === 0}
          >
            {send.isPending ? "Preparing…" : "Create customer link"}
          </button>
          <p className="tiny muted" style={{ marginTop: 10 }}>
            Makes a page the customer can open and accept from their phone. Nothing is emailed — you get a link to send
            however you normally do.
          </p>
        </>
      )}

      {/* Available before the quote has ever been sent, so it can be checked
          on paper first. Same renderer as the customer's copy — proofreading
          has to be proofreading the real thing. */}
      <p className="tiny" style={{ marginTop: 12 }}>
        {/* A button, not a link: a link cannot carry the sign-in token, and
            opened the office a 401 instead of the PDF. */}
        <button
          type="button"
          className="linkish"
          onClick={() =>
            void openDocument(`/quotes/${quote.id}/pdf`).catch((err) =>
              setProblem(err instanceof ApiError ? err.message : "Couldn't open the PDF."),
            )
          }
        >
          Open the PDF
        </button>{" "}
        — exactly what the customer sees.
      </p>
    </Card>
  );
}

/** What the quote became (round 12: the office couldn't see it). */
function QuoteJobs({ quoteId }: { quoteId: string }) {
  const jobs = useQuery({ queryKey: ["jobs", "for-quote", quoteId], queryFn: () => api.jobs.list({ quote_id: quoteId }) });
  return (
    <Card title="Became these jobs" subtitle={jobs.data ? String(jobs.data.length) : undefined}>
      {jobs.isPending ? (
        <p className="tiny muted">Looking…</p>
      ) : jobs.error ? (
        <p className="tiny muted">Couldn't load its jobs.</p>
      ) : jobs.data.length === 0 ? (
        <p className="tiny muted">No jobs — this quote was marked accepted without being accepted here.</p>
      ) : (
        <ul className="plainlist">
          {jobs.data.map((job) => (
            <li key={job.id}>
              <Link to={`/jobs/${job.id}`} className="mono">
                {job.number}
              </Link>{" "}
              {job.title} <span className="tiny muted">· {titleCase(job.status)}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
